import type {
  ChoicePrompt,
  LiveCompanion,
  MediaAction,
  MediaAiring,
  MediaBadge,
  MediaCard,
  MediaImage,
  SearchResults,
} from './media';
import { bool, isRecord, num, records, text, textList, texts } from './wire';

/**
 * What a tool payload turned into: either a shelf of media, or a question to answer.
 * Anything unrecognized maps to null and is dropped.
 */
export type ToolPayloadBlock =
  { kind: 'results'; results: SearchResults } | { kind: 'choice'; choice: ChoicePrompt };

/** Every payload type this client knows how to render, for the debug panel's coverage readout. */
export const KNOWN_PAYLOAD_TYPES = [
  'mediaList',
  'choicePrompt',
  'MEDIA_SEARCH_RESULTS',
  'DiogenesResults',
  'FollowupChips',
] as const;

/**
 * Maps a tool payload into the block the conversation renders, keyed by the payload type the
 * agent declared. The type is the whole contract: it is what selects the render template, so an
 * unknown one is dropped rather than guessed at.
 *
 * Every field below the type is optional in practice. The content-discovery agent emits a field
 * only when the catalog carries it, so absence means "we don't have this", never "empty" — hence
 * nulls rather than defaults, and rendering decisions left to the template.
 *
 * The payload arrives as `unknown` and is narrowed field by field on the way through: it is a tool
 * result from a backend this client does not own, so a field of the wrong type reads as absent
 * rather than being taken at its word.
 */
export function toPayloadBlock(
  type: string | undefined,
  payload: unknown,
): ToolPayloadBlock | null {
  const fields = isRecord(payload) ? payload : {};
  switch (type) {
    case 'mediaList':
      return { kind: 'results', results: mediaList(fields) };
    case 'choicePrompt':
      return { kind: 'choice', choice: choicePrompt(fields) };
    case 'MEDIA_SEARCH_RESULTS':
      return { kind: 'results', results: mediaSearchResults(fields) };
    case 'DiogenesResults':
      return { kind: 'results', results: diogenesResults(fields) };
    case 'FollowupChips':
      return { kind: 'results', results: followupChips(fields) };
    default:
      return null;
  }
}

function mediaList(payload: Record<string, unknown>): SearchResults {
  const cards = toCards(payload.items);
  const companion = isRecord(payload.liveCompanion) ? payload.liveCompanion : null;
  const companionCards = toCards(companion?.items);
  const totalCount = num(payload.totalCount);
  const liveCompanion: LiveCompanion | null = companionCards.length
    ? { label: text(companion?.label) ?? 'On TV now', cards: companionCards }
    : null;

  return {
    term: '',
    cards,
    suggestedChips: texts(payload.chips),
    hasMoreContent: totalCount != null && totalCount > cards.length,
    totalCount,
    // Spotlight is additive: cards[0] is the title that was asked for and the rest are
    // similar. Ignoring it degrades to a plain shelf, so an unknown value is treated as absent.
    presentation: payload.presentation === 'spotlight' ? 'spotlight' : null,
    liveCompanion,
  };
}

function choicePrompt(payload: Record<string, unknown>): ChoicePrompt {
  return {
    prompt: text(payload.prompt) ?? '',
    options: records(payload.options).flatMap((option) => {
      const label = text(option.label);
      return label === null ? [] : [{ optionId: text(option.optionId), label }];
    }),
  };
}

function toCards(items: unknown): MediaCard[] {
  return records(items).flatMap((item) => {
    const title = text(item.title);
    return title === null ? [] : [toCard(item, title)];
  });
}

function toCard(item: Record<string, unknown>, title: string): MediaCard {
  const poster = pickImage(item.images);
  return {
    id: cardId(item.id, title),
    title,
    contentType: text(item.contentType),
    playable: bool(item.playable),
    offer: text(item.offer),
    year: num(item.year),
    subtitle: text(item.subtitle),
    genres: textList(item.genres),
    cast: textList(item.cast),
    // The agent sends a single director as a string; older tools send a list.
    director:
      typeof item.director === 'string' ? textList([item.director]) : textList(item.director),
    synopsis: text(item.synopsis),
    moods: textList(item.moods),
    themes: textList(item.themes),
    rankReason: text(item.rankReason),
    posterUrl: poster?.url ?? null,
    posterAspect: poster?.aspect ?? null,
    badges: toBadges(item.badges),
    actions: toActions(item.actions),
    airing: toAiring(item.airing),
  };
}

/** The session handle the agent gave the card, falling back to its title when it gave none. */
function cardId(value: unknown, title: string): string {
  const id = num(value);
  return text(value) ?? (id === null ? title : String(id));
}

/** Whatever art exists, preferring the role the agent calls the poster. */
function pickImage(images: unknown): MediaImage | null {
  const usable: MediaImage[] = records(images).flatMap((image) => {
    const url = text(image.url);
    return url === null ? [] : [{ url, role: text(image.role), aspect: text(image.aspect) }];
  });
  return usable.find((image) => image.role === 'poster') ?? usable[0] ?? null;
}

function toBadges(badges: unknown): MediaBadge[] | null {
  const usable: MediaBadge[] = records(badges).flatMap((badge) => {
    const label = text(badge.text);
    return label === null ? [] : [{ kind: text(badge.kind), text: label }];
  });
  return usable.length ? usable : null;
}

function toActions(actions: unknown): MediaAction[] | null {
  const usable: MediaAction[] = records(actions).flatMap((action) => {
    const name = text(action.action);
    if (name === null) return [];
    return [
      {
        action: name,
        label: text(action.label),
        uri: text(action.uri),
        params: isRecord(action.params) ? action.params : null,
        primary: bool(action.primary),
      },
    ];
  });
  return usable.length ? usable : null;
}

/** Broadcast details, field by field: the channel number alone arrives both named and numbered. */
function toAiring(airing: unknown): MediaAiring | null {
  if (!isRecord(airing)) return null;
  return {
    channelName: text(airing.channelName),
    channelNumber: text(airing.channelNumber) ?? num(airing.channelNumber),
    isLive: bool(airing.isLive),
    startTime: text(airing.startTime),
    startsInMinutes: num(airing.startsInMinutes),
    isLocal: bool(airing.isLocal),
    market: text(airing.market),
    watchingNow: num(airing.watchingNow),
    score: text(airing.score),
  };
}

/** Rich media metadata from the MCP media-search tool, normalized onto the same card. */
function mediaSearchResults(payload: Record<string, unknown>): SearchResults {
  const raw = [...records(payload.contentCards), ...records(payload.rentalCards)];
  const cards: MediaCard[] = raw.flatMap((card) => {
    const title = text(card.title);
    if (title === null) return [];

    const genre = text(card.genre);
    const year = num(card.year);
    const entitlement = text(card.entitlement);
    const price = amount(card.price);
    const rating = amount(card.imdbRating);

    const badges: MediaBadge[] = [];
    if (entitlement) badges.push({ kind: 'entitlement', text: entitlement });
    if (price !== null) badges.push({ kind: 'price', text: `Rent $${price}` });
    if (rating !== null) badges.push({ kind: 'rating', text: `★ ${rating}` });

    return [
      {
        id: cardId(card.id, title),
        title,
        contentType: text(card.contentType),
        playable: null,
        offer: entitlement,
        year,
        subtitle: [year, genre].filter((part) => part !== null).join(' · ') || null,
        genres: textList(card.allGenres) ?? (genre === null ? null : [genre]),
        cast: textList(card.cast),
        director: textList(card.director),
        synopsis: text(card.description) ?? text(card.tagline),
        moods: null,
        themes: null,
        rankReason: null,
        posterUrl: text(card.posterUrl),
        posterAspect: null,
        badges: badges.length ? badges : null,
        actions: null,
        airing: null,
      },
    ];
  });

  return {
    term: text(payload.term) ?? '',
    cards,
    suggestedChips: texts(payload.suggestedChips),
    hasMoreContent: payload.hasMoreContent === true,
    totalCount: null,
    presentation: null,
    liveCompanion: null,
  };
}

/** A price or a rating, which this tool sends as a number on one card and as a string on the next. */
function amount(value: unknown): string | null {
  const parsed = num(value);
  return parsed === null ? text(value) : String(parsed);
}

/** Diogenes search: a simpler { display_list: [{ id, title, images, display_text, ... }] }. */
function diogenesResults(payload: Record<string, unknown>): SearchResults {
  const cards: MediaCard[] = records(payload.display_list).flatMap((result) => {
    const id = cardId(result.id, '');
    const title = text(result.title);
    if (!id || title === null) return [];
    return [
      {
        id,
        title,
        posterUrl: diogenesPoster(result.images),
        subtitle: text(result.display_text2),
        synopsis: text(result.display_text),
      },
    ];
  });
  return {
    term: '',
    cards,
    suggestedChips: [],
    hasMoreContent: false,
    totalCount: null,
    presentation: null,
    liveCompanion: null,
  };
}

/** This tool's art is a bare url on some rows and an image object on others. */
function diogenesPoster(images: unknown): string | null {
  if (!Array.isArray(images)) return null;
  const first: unknown = images[0];
  return text(first) ?? (isRecord(first) ? text(first.url) : null);
}

/** Chips with nothing else to them: their own payload when the turn had no results to show. */
function followupChips(payload: Record<string, unknown>): SearchResults {
  return {
    term: '',
    cards: [],
    suggestedChips: texts(payload.suggestions),
    hasMoreContent: false,
    totalCount: null,
    presentation: null,
    liveCompanion: null,
  };
}
